import React, { useRef, useEffect, useState } from 'react';
import GlobeGL from 'react-globe.gl';

export default function Globe({ flights }) {
  const globeEl = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Handle resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    };

    // Initial size
    updateDimensions();

    // Add resize listener
    window.addEventListener('resize', updateDimensions);

    // Cleanup
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    // Focus on the center of the most recently added flight
    if (globeEl.current && flights.length > 0) {
      const lastFlight = flights[flights.length - 1];

      // Calculate geographic midpoint between origin and destination
      const coords = [
        { lat: lastFlight.origin.latitude, lng: lastFlight.origin.longitude },
        { lat: lastFlight.destination.latitude, lng: lastFlight.destination.longitude }
      ];

      // Convert to Cartesian and average (handles longitude wrapping)
      let x = 0, y = 0, z = 0;
      coords.forEach(coord => {
        const latRad = coord.lat * Math.PI / 180;
        const lngRad = coord.lng * Math.PI / 180;
        x += Math.cos(latRad) * Math.cos(lngRad);
        y += Math.cos(latRad) * Math.sin(lngRad);
        z += Math.sin(latRad);
      });

      x /= coords.length;
      y /= coords.length;
      z /= coords.length;

      // Convert back to lat/lng
      const centerLng = Math.atan2(y, x) * 180 / Math.PI;
      const centerLat = Math.atan2(z, Math.sqrt(x * x + y * y)) * 180 / Math.PI;

      globeEl.current.pointOfView({ lat: centerLat, lng: centerLng, altitude: 2 }, 1000);
    }
  }, [flights]);

  // Convert flights to arcs data
  const arcsData = flights.map((flight, index) => ({
    startLat: flight.origin.latitude,
    startLng: flight.origin.longitude,
    endLat: flight.destination.latitude,
    endLng: flight.destination.longitude,
    color: getColorForFareClass(flight.fare_class),
    // Add flight data for tooltips
    originCode: flight.origin.code,
    destinationCode: flight.destination.code,
    originCity: flight.origin.city,
    destinationCity: flight.destination.city,
    fareClass: flight.fare_class,
    distance: flight.distance_miles,
    points: flight.status_points,
  }));

  // Get unique airports for labels
  const airportLabels = [];
  const seen = new Set();
  flights.forEach(flight => {
    if (!seen.has(flight.origin.code)) {
      seen.add(flight.origin.code);
      airportLabels.push({
        lat: flight.origin.latitude,
        lng: flight.origin.longitude,
        text: flight.origin.code,
        size: 1.5,
        color: '#FFFFFF'
      });
    }
    if (!seen.has(flight.destination.code)) {
      seen.add(flight.destination.code);
      airportLabels.push({
        lat: flight.destination.latitude,
        lng: flight.destination.longitude,
        text: flight.destination.code,
        size: 1.5,
        color: '#FFFFFF'
      });
    }
  });

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <GlobeGL
        ref={globeEl}
        width={dimensions.width}
        height={dimensions.height}
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
        backgroundColor="#0A1F44"
        arcsData={arcsData}
        arcColor="color"
        arcStroke={1.5}
        arcAltitudeAutoScale={0.3}
        arcLabel={(arc) => {
          const cityInfo = arc.originCity && arc.destinationCity
            ? `${arc.originCity} → ${arc.destinationCity}<br/>`
            : '';
          return `<div style="background: rgba(0,0,0,0.8); color: white; padding: 8px 12px; border-radius: 6px; font-family: system-ui; font-size: 12px; line-height: 1.5;">
            <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">${arc.originCode} → ${arc.destinationCode}</div>
            ${cityInfo}
            <div style="color: #94a3b8;">Distance: ${arc.distance.toLocaleString()} mi</div>
            <div style="color: #94a3b8;">Class: ${arc.fareClass.charAt(0).toUpperCase() + arc.fareClass.slice(1)}</div>
            <div style="color: #3b82f6; font-weight: 600;">Points: ${arc.points.toLocaleString()}</div>
          </div>`;
        }}
        labelsData={airportLabels}
        labelLat="lat"
        labelLng="lng"
        labelText="text"
        labelSize="size"
        labelColor="color"
        labelAltitude={0.01}
        enablePointerInteraction={true}
        rendererConfig={{ antialias: false, alpha: false }}
        animateIn={false}
      />
    </div>
  );
}

function getColorForFareClass(fareClass) {
  const colors = {
    basic: '#6B7280',      // Gray
    economy: '#3B82F6',    // Blue
    premium: '#8B5CF6',    // Purple
    business: '#F59E0B',   // Amber
    first: '#EAB308',      // Yellow/Gold
  };
  return colors[fareClass] || colors.economy;
}
